import type { AnswerRefAlias } from '@agavi/blocks';

/**
 * THE BENCH'S STAND-INS, READ AS ANSWERS — so one pick moves everything that depends on it.
 *
 * `testAnswers` is bench state keyed by ALIAS: what the teacher said an earlier answer was,
 * for testing, never persisted. The chips have read it since they shipped. The DRIVEN axis
 * did not, so setting a chip's value left the question underneath it unchanged — and jumping
 * into a task rather than walking to it meant there was no other way to say what the earlier
 * answer had been. Reported as "what should be loaded if there is no previous answer ... for
 * the display chips, we have the answer ... but what about for the hidden machinery?"
 *
 * It drops straight in because `sampleCorrectAnswer` records a stand-in in the BLOCK'S OWN
 * answer shape — the same thing `lastAnswersFor` returns — rather than in a resolved one.
 *
 * ## It outranks the session
 *
 * A stand-in exists only where somebody set it, which makes it an explicit "pretend they
 * answered this"; a run that already holds a real answer is exactly when that is worth
 * saying. The reverse precedence would make the control silently inert mid-run, which is the
 * worse failure of the two.
 */
export function standInAnswers(
  aliases: readonly AnswerRefAlias[],
  testAnswers: Record<string, unknown> | undefined,
): {
  /** The stand-in covering this address, or undefined — pass as `answerAt`'s first choice. */
  at: (taskId: string, blockId: string) => unknown;
} {
  const set = aliases.filter(a => testAnswers?.[a.id] !== undefined);
  /*
   * AND THE ONES KEYED BY ADDRESS. A block-address driver has no alias to key on, and
   * creating one on the first pick would write to the document for a test click — which the
   * orphan sweep would then undo, since no chip cites it. So the recall row keys those on
   * `taskId:blockId`; see `driverStandInKey`. The two spaces cannot collide.
   */
  const direct = (taskId: string, blockId: string) => testAnswers?.[`${taskId}:${blockId}`];
  return {
    at: (taskId, blockId) => {
      const alias = set.find(a =>
        a.members.some(m => m.taskId === taskId && m.blockId === blockId));
      return alias ? testAnswers?.[alias.id] : direct(taskId, blockId);
    },
  };
}
